import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Collapse,
  Tag,
  Avatar,
  Divider
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FolderOutlined,
  QuestionCircleOutlined,
  SendOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from './PageHeader';
import QuestionCard from './QuestionCard';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const QuestionnaireViewer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load questionnaire data
  useEffect(() => {
    const loadQuestionnaire = () => {
      // Mock loading questionnaire
      const mockQuestionnaire = {
        id: parseInt(id),
        title: "Employee Onboarding Quiz",
        description: "Comprehensive quiz for new employees covering company policies and procedures",
        createdBy: "John Smith",
        createdAt: "2024-10-01T10:30:00Z",
        lastUpdated: "2024-10-05T15:45:00Z",
        status: "completed",
        sections: [
          {
            id: 1,
            title: "Company Policies",
            questions: [
              {
                id: 1,
                text: "What is the standard work week at our company?",
                type: "single-choice",
                answers: [
                  { id: 1, text: "40 hours", isCorrect: true, image: null },
                  { id: 2, text: "35 hours", isCorrect: false, image: null },
                  { id: 3, text: "45 hours", isCorrect: false, image: null },
                  { id: 4, text: "50 hours", isCorrect: false, image: null }
                ]
              },
              {
                id: 2,
                text: "What is our company's policy on remote work?",
                type: "multiple-choice",
                answers: [
                  { id: 1, text: "Fully remote allowed", isCorrect: false, image: null },
                  { id: 2, text: "Hybrid model with 3 days in office", isCorrect: true, image: null },
                  { id: 3, text: "No remote work allowed", isCorrect: false, image: null }
                ]
              }
            ]
          },
          {
            id: 2,
            title: "Safety Procedures",
            questions: [
              {
                id: 3,
                text: "In case of fire emergency, what should you do first?",
                type: "single-choice",
                answers: [
                  { id: 1, text: "Use elevators to evacuate quickly", isCorrect: false, image: null },
                  { id: 2, text: "Alert others and follow evacuation route", isCorrect: true, image: null },
                  { id: 3, text: "Collect personal belongings", isCorrect: false, image: null },
                  { id: 4, text: "Fight the fire yourself", isCorrect: false, image: null }
                ]
              }
            ]
          },
          {
            id: 3,
            title: "Benefits and HR",
            questions: [
              {
                id: 4,
                text: "How many vacation days do new employees receive annually?",
                type: "select-list",
                answers: [
                  { id: 1, text: "10 days", isCorrect: false, image: null },
                  { id: 2, text: "15 days", isCorrect: true, image: null },
                  { id: 3, text: "20 days", isCorrect: false, image: null },
                  { id: 4, text: "25 days", isCorrect: false, image: null }
                ]
              }
            ]
          }
        ]
      };
      
      setQuestionnaire(mockQuestionnaire);
      setLoading(false);
    };

    loadQuestionnaire();
  }, [id]);

  const handleEdit = () => {
    navigate(`/dashboard/questionnaires/edit/${id}`);
  };

  const handleBack = () => {
    navigate('/dashboard/questionnaires');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'completed':
      case 'complete':
        return <Tag color="success" icon={<CheckCircleOutlined />}>Complete</Tag>;
      case 'published':
        return <Tag color="blue" icon={<SendOutlined />}>Published</Tag>;
      case 'draft':
        return <Tag color="warning" icon={<EditOutlined />}>Draft</Tag>;
      case 'archived':
        return <Tag color="default" icon={<InboxOutlined />}>Archived</Tag>;
      case 'editing':
        return <Tag color="processing" icon={<ClockCircleOutlined />}>Editing</Tag>;
      default:
        return <Tag color="default">Unknown</Tag>;
    }
  };

  const getTotalQuestions = () => {
    if (!questionnaire) return 0;
    return questionnaire.sections.reduce((total, section) => total + section.questions.length, 0);
  };

  const getQuestionsWithCorrectAnswers = () => {
    if (!questionnaire) return 0;
    return questionnaire.sections.reduce((total, section) => {
      return total + section.questions.filter(q => q.answers.some(a => a.isCorrect)).length;
    }, 0);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <Text>Loading quiz...</Text>
        </Card>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div style={{ padding: '24px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={4}>Quiz not found</Title>
          <Text type="secondary">The quiz you're looking for doesn't exist.</Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={handleBack}>
              Back to Quizzes
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const SectionHeader = ({ section }) => (
    <Space size="middle" style={{ width: '100%' }}>
      <FolderOutlined style={{ color: '#2E86AB' }} />
      <Text strong style={{ fontSize: '16px' }}>{section.title}</Text>
      <Tag color="blue">{section.questions.length} questions</Tag>
    </Space>
  );

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title={questionnaire.title}
        subtitle="View quiz details and structure"
        breadcrumbs={BREADCRUMB_PATTERNS.questionnaireView(questionnaire.title)}
        extra={[
          <Button key="back" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Back to List
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit Quiz
          </Button>
        ]}
      />

      {/* Questionnaire Info */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]}>
          <Col span={24}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
                  Description
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Text style={{ fontSize: '16px' }}>
                    {questionnaire.description || 'No description provided'}
                  </Text>
                </div>
              </div>
              
              <Divider style={{ margin: '16px 0' }} />
              
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Space>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        Created by
                      </Text>
                      <Text strong>{questionnaire.createdBy}</Text>
                    </div>
                  </Space>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <Space>
                    <ClockCircleOutlined style={{ color: '#999' }} />
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        Last Updated
                      </Text>
                      <Text strong>{formatDate(questionnaire.lastUpdated)}</Text>
                    </div>
                  </Space>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      Status
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      {getStatusTag(questionnaire.status)}
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      Created
                    </Text>
                    <Text strong>{formatDate(questionnaire.createdAt)}</Text>
                  </div>
                </Col>
              </Row>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Statistics */}
      <Card size="small" style={{ marginBottom: 24, backgroundColor: '#f8f9fa' }}>
        <Row gutter={24} align="middle">
          <Col xs={24} sm={6}>
            <Space>
              <FolderOutlined style={{ color: '#2E86AB' }} />
              <div>
                <Text strong style={{ fontSize: '18px' }}>{questionnaire.sections.length}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>Sections</Text>
              </div>
            </Space>
          </Col>
          <Col xs={24} sm={6}>
            <Space>
              <QuestionCircleOutlined style={{ color: '#52c41a' }} />
              <div>
                <Text strong style={{ fontSize: '18px' }}>{getTotalQuestions()}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>Total Questions</Text>
              </div>
            </Space>
          </Col>
          <Col xs={24} sm={6}>
            <Space>
              <CheckCircleOutlined style={{ color: '#faad14' }} />
              <div>
                <Text strong style={{ fontSize: '18px' }}>{getQuestionsWithCorrectAnswers()}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>With Correct Answers</Text>
              </div>
            </Space>
          </Col>
          <Col xs={24} sm={6}>
            <Space>
              <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#722ed1' }} />
              <div>
                <Text strong style={{ fontSize: '18px' }}>
                  {questionnaire.sections.reduce((total, section) => 
                    total + section.questions.reduce((qTotal, question) => 
                      qTotal + question.answers.length, 0), 0
                  )}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>Total Answers</Text>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Sections */}
      <Card title="Quiz Content">
        {questionnaire.sections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">This quiz has no sections</Text>
          </div>
        ) : (
          <Collapse 
            defaultActiveKey={questionnaire.sections.map(s => s.id.toString())}
            expandIconPosition="end"
          >
            {questionnaire.sections.map((section) => (
              <Panel
                key={section.id.toString()}
                header={<SectionHeader section={section} />}
              >
                <div style={{ padding: '16px 0' }}>
                  {section.questions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
                      <Text type="secondary">No questions in this section</Text>
                    </div>
                  ) : (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                      {section.questions.map((question, questionIndex) => (
                        <QuestionCard
                          key={question.id || questionIndex}
                          question={question}
                          questionIndex={questionIndex}
                          onQuestionChange={() => {}} // No editing in view mode
                          onDeleteQuestion={() => {}} // No editing in view mode
                          isEditing={false}
                        />
                      ))}
                    </Space>
                  )}
                </div>
              </Panel>
            ))}
          </Collapse>
        )}
      </Card>
    </div>
  );
};

export default QuestionnaireViewer;